import { useApolloClient } from "@apollo/client";
import { useFlags } from "flagsmith/react";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { CHUNKED_BATCH_SIZE } from "src/constants";
import {
  FETCH_FORM_RECIPIENTS,
  FETCH_FORM_RECIPIENTS_MAX_INPUT_SIZE,
} from "src/scenes/orgAdmin/forms/graphql/queries";
import { asyncFlatMap } from "src/services/asyncHelpers";
import { isNotNull } from "src/services/predicates";
import * as GQL from "src/types/graphql";
import { useOrganization } from "./useOrganization";

export type FormRecipients = {
  formId: uuid;
  applicant: GQL.FetchFormRecipients_form["applicant"];
  recipients: GQL.FetchFormRecipients_form_applicant_guardians_guardian[];
};

export const useGetMessageRecipients = (
  formIds: uuid[]
): [FormRecipients[], boolean] => {
  const organization = useOrganization();
  const flags = useFlags(["unverified-communications"]);
  const client = useApolloClient();

  const [formRecipients, setFormRecipients] = useState<FormRecipients[]>([]);

  const organizationId = useMemo(
    () => organization.map((org) => org.id).withDefault(""),
    [organization]
  );

  const isUnverifiedCommsEnabledForOrg =
    flags["unverified-communications"].enabled;

  useEffect(() => {
    if (!organizationId) return;

    const uniqueFormIds = _.uniq(formIds);
    asyncFlatMap(
      _.chunk(uniqueFormIds, FETCH_FORM_RECIPIENTS_MAX_INPUT_SIZE),
      async (formsIdChunk) => {
        const result = await client.query<
          GQL.FetchFormRecipients,
          GQL.FetchFormRecipientsVariables
        >({
          query: FETCH_FORM_RECIPIENTS,
          variables: {
            organization_id: organizationId,
            form_ids: formsIdChunk,
          },
        });
        if (result.error) throw result.error;
        if (!result.data) throw new Error("data");
        return result.data.form;
      },
      CHUNKED_BATCH_SIZE
    ).then((records) => {
      const result = records.map((record) => ({
        formId: record.id,
        applicant: record.applicant,
        recipients: record.applicant_guardians
          .map(({ guardian }) => guardian)
          .filter(
            (guardian) =>
              guardian?.sms_okay ||
              guardian?.email_okay ||
              isUnverifiedCommsEnabledForOrg
          )
          .filter(isNotNull),
      })) satisfies FormRecipients[];

      setFormRecipients(result);
    });
  }, [client, isUnverifiedCommsEnabledForOrg, formIds, organizationId]);

  return [formRecipients, isUnverifiedCommsEnabledForOrg];
};
